// src/data/examShowcase.js
//
// Content for the scroll showcase at the top of /:lang/mock-exams/:slug.
//
// The showcase replays the exam's FREE paper (src/data/publishablePapers.js), the
// one a visitor actually gets from "Start a free mock exam", so what the page shows
// is what they sit. Everything in an entry has to be true of THAT exam: its parts,
// task types, clocks, how often audio plays, its pass rules, and whether the paper
// explains its answers. The formats really differ (Goethe has no Sprachbausteine
// and scores each module; ÖSD B2 has the cut-off right edge; telc C1 rebuilds a
// text from sentences), so nothing here is shared across exams.
//
// Quoted exam content is copied from the named `source` file. Only the sample
// letters in the writing chapters are written for the demo.
//
// Scene types (src/components/mockExam/ExamShowcase.jsx):
//   matching  - headlines to a text; one drops into the slot
//   gap       - one gap: picked from options (inline or `layout: 'stack'` for
//               whole sentences) or, with no options, typed
//   questions - rows of richtig/falsch or a/b/c, under `audio` or a `reading`
//   match     - option list + rows whose slots fill with a letter
//   endings   - ÖSD's cut-off right edge, endings typed back in
//   writing   - task + sample letter typing itself, live word count
//   results   - sample points, labelled "Beispiel", plus one real explanation.
//               `pass` on a bar = a real per-part minimum; leave it out where
//               only the total counts (telc, ÖSD B1).
//
// Captions (title/body) are English defaults for t(`mockExam.showcase.<slug>.<id>.*`).
// Audio facts in captions come from each part's `audio.playCount`; explanation
// claims come from counting `explanation` per section in the source paper.

const TF = ['richtig', 'falsch'];
const TF_GOETHE = ['Richtig', 'Falsch'];

export const EXAM_SHOWCASE = {
  'telc-b1': {
    source: 'telc/b1/telc-deutsch-b1-mock-001 - Logami 1.json',
    paperLabel: 'telc Deutsch B1 · Practice paper 1',
    chapters: [
      {
        id: 'reading', scene: 'matching', nav: 'Leseverstehen', section: 'Leseverstehen · Teil 1', clockSeconds: 90 * 60,
        title: 'Read the way the exam makes you read.',
        body: 'Five texts, ten headlines, one 90-minute clock shared with Sprachbausteine.',
        textLabel: 'Text 1',
        text: 'Bohrmaschine, Leiter, Säge, Schleifgerät – die meisten Menschen brauchen solche Werkzeuge nur selten, kaufen sie aber trotzdem. In Köln gibt es seit diesem Jahr eine Alternative: die Werkzeugbibliothek im Stadtteil Ehrenfeld.',
        optionsLabel: 'Überschriften a–j',
        options: [
          { id: 'a', text: 'Öffentliche Kühlschränke: Lebensmittel für alle gratis verfügbar' },
          { id: 'b', text: 'Städte stellen Familien kostenlos Lastenfahrräder zur Verfügung' },
          { id: 'c', text: 'Senioren entdecken das gemeinsame Wohnen für sich' },
          { id: 'd', text: 'Bibliothek erweitert Angebot um technische Fachliteratur' },
          { id: 'e', text: 'Werkzeuge leihen statt kaufen: Neues Angebot für Heimwerker' },
        ],
        answer: 'e',
      },
      {
        id: 'grammar', scene: 'gap', nav: 'Sprachbausteine', section: 'Sprachbausteine · Teil 1', clockSeconds: 61 * 60 + 40,
        title: 'Every gap, in context.',
        body: 'Pick an answer and it drops straight into the letter, so you read the sentence the way the examiner will.',
        gapNumber: 21, gapLabel: 'Lücke 21',
        before: 'Liebe Kati, ich schreibe dir aus München, wo ich jetzt seit einem Monat wohne. Der Umzug war anstrengend, aber ich habe mich schon gut',
        after: 'meine neue Umgebung gewöhnt.',
        options: [{ id: 'a', text: 'auf' }, { id: 'b', text: 'an' }, { id: 'c', text: 'in' }],
        answer: 'b',
      },
      {
        id: 'listening', scene: 'questions', nav: 'Hörverstehen', section: 'Hörverstehen · Teil 1', clockSeconds: 30 * 60,
        title: 'Hear it once. Like on the day.',
        body: 'Teil 1 is played once, as in the real exam. There is no pause button and no rewinding.',
        audio: { label: 'Kurze Texte', note: 'Sie hören diese Texte nur einmal.' },
        rows: [
          { num: 41, text: 'Die Sprecherin verbringt ihren Urlaub jedes Jahr am selben Ort.', labels: TF, answer: 0 },
          { num: 42, text: 'Der Sprecher ist im Urlaub mit dem Fahrrad durch Österreich gefahren.', labels: TF, answer: 1 },
        ],
      },
      {
        id: 'writing', scene: 'writing', nav: 'Schriftlicher Ausdruck', section: 'Schriftlicher Ausdruck', clockSeconds: 30 * 60,
        title: 'Answer the email, against the clock.',
        body: 'A real email task, 30 minutes and a live word count. Submit it and you get a score and feedback.',
        taskLabel: 'E-Mail · alle vier Punkte',
        task: 'Ihre Kollegin Katrin schreibt: Frau Sandmann verlässt die Abteilung. Antworten Sie: welches Geschenk, wie viel Geld jeder gibt, wann und wie Sie es übergeben, wer noch helfen könnte.',
        wordGoal: 'mind. 80',
        letter: 'Liebe Katrin,\n\nvielen Dank für deine E-Mail! Ich finde es auch schade, dass Frau Sandmann uns verlässt. Wie wäre es mit einem Gutschein für ein schönes Restaurant? Dann kann sie in Ruhe mit ihrer Familie essen gehen. Ich denke, jeder könnte 15 Euro dazugeben.',
      },
      {
        id: 'results', scene: 'results', nav: 'Ergebnis', section: 'Ergebnis', clockSeconds: null,
        title: 'Know exactly where you stand.',
        body: 'Points for every written section against the 60 % pass mark, and an explanation for every answer.',
        showTotal: true, verdict: 'Bestanden · {{pct}} %',
        bars: [
          { name: 'Leseverstehen', got: 60, max: 75 },
          { name: 'Sprachbausteine', got: 24, max: 30 },
          { name: 'Hörverstehen', got: 58, max: 75 },
          { name: 'Schriftlicher Ausdruck', got: 33, max: 45 },
        ],
        explanation: {
          label: 'Lücke 21', answer: 'b',
          text: 'Richtig ist „an“: „sich gewöhnen an + Akkusativ“ ist eine feste Verbindung. „auf“ passt zu „sich freuen auf“, nicht zu „sich gewöhnen“.',
        },
      },
    ],
    redPen: {
      level: 'b1',
      redemittel: { label: 'Redemittel · Sprechen Teil 3', phrase: 'Das ist eine gute Idee.', note: 'zustimmen', meaning: "That's a good idea.", count: 84 },
    },
  },

  'telc-b2': {
    source: 'telc/b2/telc-deutsch-b2-mock-001 - Logami 1.json',
    paperLabel: 'telc Deutsch B2 · Practice paper 1',
    chapters: [
      {
        id: 'reading', scene: 'matching', nav: 'Leseverstehen', section: 'Leseverstehen · Teil 1', clockSeconds: 90 * 60,
        title: 'Read the way the exam makes you read.',
        body: 'Five texts, ten headlines, one 90-minute clock shared with Sprachbausteine.',
        textLabel: 'Text 1',
        text: 'Um die Akzeptanz von Elektrofahrzeugen auch außerhalb städtischer Ballungsräume zu steigern, hat die Bundesregierung ein Sonderprogramm zur Ladeinfrastruktur in Kommunen unter 20.000 Einwohnern aufgelegt. Gefördert werden öffentliche Schnellladesäulen an zentralen Standorten wie Rathäusern und Bahnhöfen.',
        optionsLabel: 'Überschriften a–j',
        options: [
          { id: 'a', text: 'Studie belegt steigendes Unfallrisiko durch mangelnde Radwegkontinuität' },
          { id: 'b', text: 'Investitionsstau gefährdet den geplanten Ausbau des Schienennetzes' },
          { id: 'c', text: 'Nahverkehrsverbände warnen vor Betriebseinschränkungen wegen Lokführermangels' },
          { id: 'd', text: 'Stadtrat stimmt gegen Ausweisung einer autofreien Innenstadt' },
          { id: 'e', text: 'Ladeinfrastruktur für Elektrofahrzeuge wird in ländlichen Regionen beschleunigt' },
        ],
        answer: 'e',
      },
      {
        id: 'grammar', scene: 'gap', nav: 'Sprachbausteine', section: 'Sprachbausteine · Teil 1', clockSeconds: 61 * 60 + 40,
        title: 'Every gap, in context.',
        body: 'Pick an answer and it drops straight into the letter, so you read the sentence the way the examiner will.',
        gapNumber: 21, gapLabel: 'Lücke 21',
        before: 'Liebe Franziska, stell dir vor: Seit zwei Monaten lebe ich in einem',
        after: 'Mehrgenerationenhaus direkt am Stadtpark. Das Projekt wurde von einer Initiative engagierter Bürgerinnen und Bürger gegründet.',
        options: [{ id: 'a', text: 'modernes' }, { id: 'b', text: 'moderner' }, { id: 'c', text: 'modernen' }],
        answer: 'c',
      },
      {
        id: 'listening', scene: 'questions', nav: 'Hörverstehen', section: 'Hörverstehen · Teil 1', clockSeconds: 20 * 60,
        title: 'Hear it once. Like on the day.',
        body: 'Teil 1 is played once, as in the real exam. There is no pause button and no rewinding.',
        audio: { label: 'Nachrichten', note: 'Sie hören die Nachrichten einmal.' },
        rows: [
          { num: 41, text: 'Ab dem kommenden Jahr dürfen Einweg-E-Zigaretten in Deutschland nicht mehr verkauft werden.', labels: TF, answer: 0 },
          { num: 42, text: 'Die Zahl der Seehunde im Wattenmeer ist im Vergleich zum Vorjahr leicht gesunken.', labels: TF, answer: 1 },
        ],
      },
      {
        id: 'writing', scene: 'writing', nav: 'Schriftlicher Ausdruck', section: 'Schriftlicher Ausdruck', clockSeconds: 30 * 60,
        title: 'Write against the clock.',
        body: 'A real letter task, 30 minutes and a live word count. Submit it and you get a score and feedback.',
        taskLabel: 'Thema 1 · E-Mail',
        task: 'Sie interessieren sich für einen Töpferkurs an der Volkshochschule Lindenau und schreiben eine E-Mail, um weitere Informationen zu erhalten.',
        wordGoal: 'mind. 150',
        letter: 'Sehr geehrte Damen und Herren,\n\nmit großem Interesse habe ich in Ihrem Programm die Anzeige für den neuen Töpferkurs gelesen. Da ich schon lange gern mit Ton arbeiten möchte, hätte ich dazu einige Fragen.\n\nZunächst würde ich gern wissen, wann der Kurs genau beginnt und wie viele Personen in einer Gruppe sind.',
      },
      {
        id: 'results', scene: 'results', nav: 'Ergebnis', section: 'Ergebnis', clockSeconds: null,
        title: 'Know exactly where you stand.',
        body: 'Points for every written section against the 60 % pass mark, with an explanation for every Leseverstehen and Sprachbausteine answer.',
        showTotal: true, verdict: 'Bestanden · {{pct}} %',
        bars: [
          { name: 'Leseverstehen', got: 63, max: 75 },
          { name: 'Sprachbausteine', got: 24, max: 30 },
          { name: 'Hörverstehen', got: 60, max: 75 },
          { name: 'Schriftlicher Ausdruck', got: 36, max: 45 },
        ],
        explanation: {
          label: 'Lücke 21', answer: 'c',
          text: 'Richtig ist c: „in einem modernen Mehrgenerationenhaus“. Nach „einem“ (Dativ Neutrum) bekommt das Adjektiv die Endung -en. „modernes“ passt zu Nominativ/Akkusativ Neutrum nach „ein“.',
        },
      },
    ],
    redPen: {
      level: 'b2',
      redemittel: { label: 'Redemittel · Sprechen Teil 3', phrase: 'Eine mögliche Lösung wäre …', note: 'sicher in Teil 3', meaning: 'One possible solution would be …', count: 72 },
    },
  },

  'telc-c1': {
    source: 'telc/c1/telc-deutsch-c1-mock-001 - Logami 1.json',
    paperLabel: 'telc Deutsch C1 Hochschule · Practice paper 1',
    chapters: [
      {
        id: 'reading', scene: 'gap', nav: 'Leseverstehen', section: 'Leseverstehen · Teil 1', clockSeconds: 90 * 60,
        title: 'Put the argument back together.',
        body: 'Six gaps, eight sentences, two left over: Teil 1 rebuilds an academic text. 90 minutes, shared with Sprachbausteine.',
        label: 'Das koloniale Erbe: Museen als politische Verhandlungsräume',
        layout: 'stack', gapNumber: 1, gapLabel: 'Lücke 1',
        before: 'Viele der betroffenen Objekte wurden in Epochen erworben, in denen das zeitgenössische Völkerrecht den kolonisierten Gesellschaften keinerlei formalen Eigentumsschutz gewährte.',
        after: 'Museen befinden sich in dieser Grauzone in einem fundamentalen Dilemma.',
        options: [
          { id: 'c', text: 'Die physische Rückgabe solcher Güter ist folglich immer auch ein politischer Akt der symbolischen Anerkennung begangenen Unrechts.' },
          { id: 'g', text: 'Eine endgültige, universelle juristische Lösung für diesen tiefgreifenden Konflikt zeichnet sich auf internationaler Ebene derzeit nicht ab.' },
          { id: 'h', text: 'Historische Legalität, so argumentieren Kritiker zu Recht, rechtfertigt aber nicht automatisch moralische und ethische Besitzansprüche in der Gegenwart.' },
        ],
        answer: 'h',
      },
      {
        id: 'grammar', scene: 'gap', nav: 'Sprachbausteine', section: 'Sprachbausteine · Teil 1', clockSeconds: 58 * 60 + 20,
        title: 'Four options, one exact phrase.',
        body: 'Each gap offers four words that look alike. Only one completes the fixed phrase.',
        gapNumber: 25, gapLabel: 'Lücke 25',
        before: 'Die Allgegenwart von Smartphones und sozialen Netzwerken',
        after: 'die Aufmerksamkeit der Leser massiv auf sich und fragmentiert den Akt der Lektüre in flüchtige, oberflächliche Konsumeinheiten.',
        options: [{ id: 'a', text: 'beansprucht' }, { id: 'b', text: 'zieht' }, { id: 'c', text: 'hält' }, { id: 'd', text: 'fordert' }],
        answer: 'b',
      },
      {
        id: 'listening', scene: 'match', nav: 'Hörverstehen', section: 'Hörverstehen · Teil 1', clockSeconds: 40 * 60,
        title: 'Eight opinions, heard once.',
        body: 'Match what each person thinks to one of ten statements. Teil 1 is played once, with no pause button.',
        audio: { label: 'Meinungen zur Mode', note: 'Sie hören die Meinungen nur einmal.' },
        optionsLabel: 'Aussagen a–j',
        options: [
          { id: 'a', text: 'Schnelle Mode verführt zu Käufen, die man kaum trägt.' },
          { id: 'c', text: 'Die Arbeitsbedingungen in der Textilproduktion sind oft menschenunwürdig.' },
          { id: 'e', text: 'Qualität lohnt sich langfristig mehr als billige Massenware.' },
          { id: 'i', text: 'Kleidung ist für mich in erster Linie ein Mittel der Selbstdarstellung.' },
        ],
        rows: [{ label: 'Person 1', answer: 'i' }, { label: 'Person 2', answer: 'c' }],
      },
      {
        id: 'writing', scene: 'writing', nav: 'Schriftlicher Ausdruck', section: 'Schriftlicher Ausdruck', clockSeconds: 70 * 60,
        title: 'A full argument in 70 minutes.',
        body: 'Choose a topic and write a structured statement of at least 350 words, with a live word count. Submit it and you get a score and feedback.',
        taskLabel: 'Thema 1 · Stellungnahme',
        task: 'An Ihrer Hochschule wird diskutiert, ob ein Auslandssemester für alle Studierenden verpflichtend werden soll. Verfassen Sie für die Universitätszeitung eine Stellungnahme zum Thema „Auslandssemester als Pflicht“.',
        wordGoal: 'mind. 350',
        letter: 'Auslandssemester als Pflicht?\n\nKaum ein Thema wird an unserer Hochschule derzeit so leidenschaftlich diskutiert wie die Frage, ob ein Semester im Ausland für alle Studierenden verpflichtend werden soll. Befürworter verweisen auf interkulturelle Kompetenz und bessere Berufschancen. So überzeugend diese Argumente zunächst wirken, so wenig berücksichtigen sie die finanzielle Lage vieler Studierender.',
      },
      {
        id: 'results', scene: 'results', nav: 'Ergebnis', section: 'Ergebnis', clockSeconds: null,
        title: 'Know exactly where you stand.',
        body: 'Points for every written section against the 60 % pass mark, with an explanation for every Leseverstehen and Sprachbausteine answer.',
        showTotal: true, verdict: 'Bestanden · {{pct}} %',
        bars: [
          { name: 'Leseverstehen', got: 38, max: 48 },
          { name: 'Sprachbausteine', got: 16, max: 22 },
          { name: 'Hörverstehen', got: 34, max: 48 },
          { name: 'Schriftlicher Ausdruck', got: 35, max: 48 },
        ],
        explanation: {
          label: 'Lücke 25', answer: 'b',
          text: 'Richtig ist „zieht“: feste Wendung „die Aufmerksamkeit auf sich ziehen“. „beanspruchen“ und „fordern“ stehen nicht mit „auf sich“, „die Aufmerksamkeit auf sich halten“ gibt es nicht.',
        },
      },
    ],
    redPen: {
      level: 'c1',
      redemittel: { label: 'Redemittel · Sprechen Teil 2', phrase: 'Darf ich dazu kurz etwas anmerken?', note: 'sich einbringen', meaning: 'May I briefly add something to that?', count: 55 },
    },
  },

  'goethe-b1': {
    source: 'goethe/b1/goethe-deutsch-b1-mock-001 - Logami 3.json',
    paperLabel: 'Goethe-Zertifikat B1 · Practice paper 1',
    chapters: [
      {
        id: 'reading', scene: 'questions', nav: 'Lesen', section: 'Lesen · Teil 1', clockSeconds: 65 * 60,
        title: 'Read, then decide: Richtig oder Falsch?',
        body: 'Teil 1 is a blog post with six statements to check against it. Lesen runs on its own 65-minute clock.',
        reading: {
          title: 'Emils Studentenblog: Mein erster Tag als Fahrradkurier',
          text: 'Seit einer Woche habe ich einen Nebenjob: Ich fahre als Kurier für eine kleine Firma, die Pakete und Briefe mit dem Fahrrad durch die Stadt bringt. Ein Freund aus meinem Studiengang hat mir dann von der Firma erzählt … Die Firma gibt uns zwar eine Regenjacke, aber nach einer Stunde war ich trotzdem nass bis auf die Haut.',
        },
        rows: [
          { num: 1, text: 'Emil hat die Stelle über einen Freund gefunden.', labels: TF_GOETHE, answer: 0 },
          { num: 2, text: 'Mit der Regenjacke der Firma blieb Emil trocken.', labels: TF_GOETHE, answer: 1 },
        ],
      },
      {
        id: 'listening', scene: 'questions', nav: 'Hören', section: 'Hören · Teil 1', clockSeconds: 40 * 60,
        title: 'Short texts, each heard twice.',
        body: 'Teil 1 plays every text twice, as in the real exam, with two tasks per text. There is no pause button.',
        audio: { label: 'Kurze Texte', note: 'Sie hören jeden Text zweimal.' },
        rows: [
          { num: 1, text: 'Die Reparatur ist teurer geworden als geplant.', labels: TF_GOETHE, answer: 1 },
          {
            num: 2, text: 'Bis wann kann man das Auto heute abholen?',
            options: [{ id: 'a', text: 'bis 8 Uhr' }, { id: 'b', text: 'bis 20 Uhr' }, { id: 'c', text: 'bis 18 Uhr' }],
            answer: 'c',
          },
        ],
      },
      {
        id: 'writing', scene: 'writing', nav: 'Schreiben', section: 'Schreiben · Teil 1', clockSeconds: 60 * 60,
        title: 'Write the email, all three points.',
        body: 'About 80 words for Teil 1, with a live word count. Schreiben runs for 60 minutes. Submit it and you get a score and feedback.',
        taskLabel: 'Teil 1 · E-Mail',
        task: 'Sie sind gestern von einer zweiwöchigen Sprachreise nach Italien zurückgekommen. Erzählen Sie einer Kollegin, was Sie erlebt haben, welche Stadt am schönsten war und wann Sie sich treffen können.',
        wordGoal: 'ca. 80',
        letter: 'Liebe Sandra,\n\nseit gestern bin ich wieder zu Hause! Die Sprachreise nach Italien war wunderbar. Am Vormittag hatten wir Unterricht, und am Nachmittag haben wir Ausflüge gemacht. Am schönsten fand ich Florenz, weil die alten Kirchen und die kleinen Gassen so beeindruckend sind.',
      },
      {
        id: 'results', scene: 'results', nav: 'Ergebnis', section: 'Ergebnis', clockSeconds: null,
        title: 'Every module on its own.',
        body: 'Goethe scores each module out of 100 and you need 60 in each. You see every module separately, with an explanation for every Lesen answer.',
        showTotal: false, heading: 'Module', verdict: 'Lesen, Hören und Schreiben bestanden',
        bars: [
          { name: 'Lesen', got: 77, max: 100, pass: 0.6 },
          { name: 'Hören', got: 70, max: 100, pass: 0.6 },
          { name: 'Schreiben', got: 68, max: 100, pass: 0.6 },
        ],
        barsNote: 'Sprechen is a separate module with its own 60-point minimum.',
        explanation: {
          label: 'Lesen · Aufgabe 2', answer: 'Falsch',
          text: 'Richtig ist Falsch. Im Text steht: „nach einer Stunde war ich trotzdem nass bis auf die Haut“. Trotz der Regenjacke wurde Emil nass.',
        },
      },
    ],
    redPen: {
      level: 'b1',
      redemittel: { label: 'Redemittel · Sprechen Teil 3', phrase: 'Danke für deine interessante Präsentation.', note: 'Feedback geben', meaning: 'Thanks for your interesting presentation.', count: 48 },
    },
  },

  'goethe-b2': {
    source: 'goethe/b2/goethe-deutsch-b2-mock-001 - Logami 6.json',
    paperLabel: 'Goethe-Zertifikat B2 · Practice paper 1',
    chapters: [
      {
        id: 'reading', scene: 'match', nav: 'Lesen', section: 'Lesen · Teil 1', clockSeconds: 65 * 60,
        title: 'Who says what?',
        body: 'Four forum posts, nine statements: match each statement to the person who makes it. Lesen runs on its own 65-minute clock.',
        optionsLabel: 'Forum: Neue Freundschaften als Erwachsene',
        options: [
          { id: 'a', text: 'Jonas: „Vor zwei Jahren bin ich wegen einer neuen Stelle nach Leipzig gezogen …“' },
          { id: 'b', text: 'Miriam: „Seit meine Kinder in die Kita und später in die Schule gehen …“' },
          { id: 'c', text: 'Dariusz: „Ich bin vor vier Jahren aus Polen nach Deutschland gekommen …“' },
          { id: 'd', text: 'Renate: „Als ich letztes Jahr in Rente gegangen bin …“' },
        ],
        reusable: true, rowsFirst: true,
        rows: [
          { label: '1 · Für wen sind die Kontakte vor allem über die Kinder entstanden?', answer: 'b' },
          { label: '2 · Wer findet es seltsam, Freunde über eine App zu suchen?', answer: 'a' },
        ],
      },
      {
        id: 'listening', scene: 'questions', nav: 'Hören', section: 'Hören · Teil 1', clockSeconds: 40 * 60,
        title: 'Heard once, two tasks per text.',
        body: 'Teil 1 plays five short texts once each, as in the real exam. No pause button, no rewinding.',
        audio: { label: 'Gespräche und Äußerungen', note: 'Sie hören jeden Text einmal.' },
        rows: [
          { num: 1, text: 'Die Sprecherin schreibt nur selten Tagebuch.', labels: TF_GOETHE, answer: 1 },
          {
            num: 2, text: 'Warum ist das Tagebuch für die Sprecherin wertvoll?',
            options: [{ id: 'a', text: 'Es war teuer.' }, { id: 'b', text: 'Es ist besonders schön.' }, { id: 'c', text: 'Es hält auch ihre Gedanken fest.' }],
            answer: 'c',
          },
        ],
      },
      {
        id: 'writing', scene: 'writing', nav: 'Schreiben', section: 'Schreiben · Teil 1', clockSeconds: 75 * 60,
        title: 'Take a position in the forum.',
        body: 'At least 150 words on four points, with a live word count. Schreiben runs for 75 minutes. Submit it and you get a score and feedback.',
        taskLabel: 'Teil 1 · Forumsbeitrag',
        task: 'Sie nehmen an einer Diskussion in einem Internet-Forum zum Thema „Digitale Medien im Alltag: Bereicherung oder Belastung?“ teil.',
        wordGoal: 'mind. 150',
        letter: 'Digitale Medien sind aus unserem Alltag kaum noch wegzudenken. Sie erleichtern vieles: Termine lassen sich mit wenigen Klicks vereinbaren, und mit Freunden im Ausland bleibt man problemlos in Kontakt.\n\nAllerdings sehe ich auch die Schattenseiten. Wer ständig erreichbar ist, kommt kaum noch zur Ruhe.',
      },
      {
        id: 'results', scene: 'results', nav: 'Ergebnis', section: 'Ergebnis', clockSeconds: null,
        title: 'Every module on its own.',
        body: 'Goethe scores each module out of 100 and you need 60 in each. You see every module separately, with an explanation for every Lesen answer.',
        showTotal: false, heading: 'Module', verdict: 'Lesen, Hören und Schreiben bestanden',
        bars: [
          { name: 'Lesen', got: 73, max: 100, pass: 0.6 },
          { name: 'Hören', got: 67, max: 100, pass: 0.6 },
          { name: 'Schreiben', got: 70, max: 100, pass: 0.6 },
        ],
        barsNote: 'Sprechen is a separate module with its own 60-point minimum.',
        explanation: {
          label: 'Lesen · Aufgabe 2', answer: 'a',
          text: 'Jonas hält „von Apps, mit denen man gezielt Freunde sucht, … wenig“; es fühlt sich für ihn „an wie ein Vorstellungsgespräch“. Dariusz sagt zwar, viele fänden das komisch, er selbst hat die App aber mit Erfolg benutzt.',
        },
      },
    ],
    redPen: {
      level: 'b2',
      redemittel: { label: 'Redemittel · Sprechen Teil 2', phrase: 'Ich würde gerne auf Ihren ersten Punkt eingehen.', note: 'auf Argumente eingehen', meaning: 'I would like to address your first point.', count: 62 },
    },
  },

  'goethe-c1': {
    source: 'goethe/c1/goethe-deutsch-c1-mock-001 - Logami 1.json',
    paperLabel: 'Goethe-Zertifikat C1 · Practice paper 1',
    chapters: [
      {
        id: 'reading', scene: 'gap', nav: 'Lesen', section: 'Lesen · Teil 1', clockSeconds: 65 * 60,
        title: 'Fill the gap the way a C1 text needs.',
        body: 'Teil 1 is a specialist article with gaps and four options each. Lesen runs on its own 65-minute clock.',
        label: 'Mentale Erschöpfung in der Wissensgesellschaft',
        gapNumber: 1, gapLabel: 'Lücke 1',
        before: 'Während frühere Generationen Erschöpfung als vorübergehenden Zustand betrachteten, stellen Medizinerinnen und Mediziner heute fest, dass viele nicht zur gewohnten Leistungsfähigkeit zurückfinden. Schuld',
        after: 'ist häufig nicht körperliche, sondern emotionale und kognitive Verausgabung.',
        options: [{ id: 'a', text: 'davon' }, { id: 'b', text: 'daran' }, { id: 'c', text: 'dafür' }, { id: 'd', text: 'dabei' }],
        answer: 'b',
      },
      {
        id: 'listening', scene: 'match', nav: 'Hören', section: 'Hören · Teil 1', clockSeconds: 40 * 60,
        title: 'Which production was it?',
        body: 'A podcast reviews three theatre productions and you hear it once. Match each statement to the right one.',
        audio: { label: 'Podcast: neue Inszenierungen', note: 'Sie hören den Text einmal.' },
        optionsLabel: 'Inszenierungen',
        options: [
          { id: 'a', text: 'Inszenierung 1 – „Der stille Gast“' },
          { id: 'b', text: 'Inszenierung 2 – „Sturm über der Stadt“' },
          { id: 'c', text: 'Inszenierung 3 – „Die Erben“' },
        ],
        reusable: true, rowsFirst: true,
        rows: [
          { label: '1 · Die Regie setzt bewusst auf eine sehr schlichte Bühne.', answer: 'a' },
          { label: '2 · Trotz des ernsten Themas wird viel gelacht.', answer: 'c' },
        ],
      },
      {
        id: 'writing', scene: 'writing', nav: 'Schreiben', section: 'Schreiben · Teil 1', clockSeconds: 75 * 60,
        title: 'A forum contribution at C1.',
        body: 'About 230 words on four points, with a live word count. Schreiben runs for 75 minutes. Submit it and you get a score and feedback.',
        taskLabel: 'Teil 1 · Forumsbeitrag',
        task: 'Für das Internetforum „Bildung & Gesellschaft“ verfassen Sie einen Diskussionsbeitrag: Lebenslanges Lernen – Notwendigkeit oder Zwang?',
        wordGoal: 'ca. 230',
        letter: 'Dass lebenslanges Lernen in der heutigen Arbeitswelt an Bedeutung gewinnt, liegt vor allem am rasanten technologischen Wandel. Kenntnisse, die vor zehn Jahren noch als Standard galten, sind heute vielfach überholt. Wer beruflich nicht den Anschluss verlieren möchte, kommt um regelmäßige Weiterbildung kaum herum.',
      },
      {
        id: 'results', scene: 'results', nav: 'Ergebnis', section: 'Ergebnis', clockSeconds: null,
        title: 'Every module on its own.',
        body: 'Goethe scores each module out of 100 and you need 60 in each. You see every module separately, with an explanation for every Lesen answer.',
        showTotal: false, heading: 'Module', verdict: 'Lesen, Hören und Schreiben bestanden',
        bars: [
          { name: 'Lesen', got: 71, max: 100, pass: 0.6 },
          { name: 'Hören', got: 64, max: 100, pass: 0.6 },
          { name: 'Schreiben', got: 69, max: 100, pass: 0.6 },
        ],
        barsNote: 'Sprechen is a separate module with its own 60-point minimum.',
        explanation: {
          label: 'Lesen · Lücke 1', answer: 'b',
          text: 'Richtig ist „daran“: „Schuld an etwas sein“, also Schuld daran ist die emotionale und kognitive Verausgabung. „Davon“, „dafür“ und „dabei“ passen nicht zu „Schuld sein“.',
        },
      },
    ],
    redPen: {
      level: 'c1',
      redemittel: { label: 'Redemittel · Sprechen Teil 2', phrase: 'Diese Argumentation weist jedoch einige problematische Aspekte auf.', note: 'widersprechen', meaning: 'However, this argument has some problematic aspects.', count: 49 },
    },
  },

  'osd-b1': {
    source: 'osd/b1/osd-deutsch-b1-mock-001 - Claude 1.json',
    paperLabel: 'ÖSD Zertifikat B1 · Practice paper 1',
    chapters: [
      {
        id: 'reading', scene: 'matching', nav: 'Leseverstehen', section: 'Leseverstehen · Teil 1', clockSeconds: 90 * 60,
        title: 'Austrian news, matched to its headline.',
        body: 'Five texts, ten headlines A–K, five left over. Leseverstehen and Sprachbausteine share 90 minutes.',
        textLabel: 'Text 1',
        text: 'Eine aktuelle Umfrage unter 1 200 Jugendlichen zeigt interessante Entwicklungen im Medienkonsum österreichischer Teenager. 78 Prozent der befragten 14- bis 18-Jährigen verbringen täglich mehr als zwei Stunden auf Plattformen wie Instagram, TikTok und Snapchat.',
        optionsLabel: 'Überschriften A–K',
        options: [
          { id: 'A', text: 'Weniger Österreicher kaufen Bio-Produkte' },
          { id: 'B', text: 'Mehr digitaler Service in Wiens Öffis' },
          { id: 'C', text: 'Jugendliche täglich stundenlang in sozialen Medien' },
          { id: 'D', text: 'Fahrpreise für Bus und U-Bahn steigen' },
          { id: 'I', text: 'Handyverbot an Österreichs Schulen geplant' },
        ],
        answer: 'C',
      },
      {
        id: 'grammar', scene: 'gap', nav: 'Sprachbausteine', section: 'Sprachbausteine · Aufgabe 1', clockSeconds: 61 * 60 + 40,
        title: 'Every gap, in context.',
        body: 'Pick an answer and it drops straight into the email, so you read the sentence the way the examiner will.',
        gapNumber: 21, gapLabel: 'Lücke 21',
        before: 'Liebe Maria, vielen Dank für deine lange E-Mail – und entschuldige bitte, dass ich mich so lange nicht',
        after: 'habe! Ich hatte in den letzten Wochen furchtbar viel zu tun.',
        options: [{ id: 'a', text: 'gemeldet' }, { id: 'b', text: 'melden' }, { id: 'c', text: 'meldete' }],
        answer: 'a',
      },
      {
        id: 'listening', scene: 'questions', nav: 'Hören', section: 'Hören · Aufgabe 1', clockSeconds: 30 * 60,
        title: 'Five opinions, heard once.',
        body: 'Five people give their view and you hear each one once, as in the real exam. Decide: richtig or falsch.',
        audio: { label: 'Stellungnahmen', note: 'Sie hören diese Texte ein Mal.' },
        rows: [
          { num: 41, text: 'Die Sprecherin findet, dass sie zu viel Zeit am Smartphone verbringt.', labels: TF, answer: 0 },
          { num: 42, text: 'Der Sprecher nutzt Social Media aktiv.', labels: TF, answer: 1 },
        ],
      },
      {
        id: 'writing', scene: 'writing', nav: 'Schreiben', section: 'Schreiben', clockSeconds: 40 * 60,
        title: 'Write back to the neighbour.',
        body: 'An email of 120 to 150 words in 40 minutes, with a live word count. Submit it and you get a score and feedback.',
        taskLabel: 'Variante A · E-Mail',
        task: 'Ihre Nachbarin Maria plant ein Sommerfest im Innenhof und bittet um Ihre Hilfe und Ideen: Band oder Playlist, Spiele für die Kinder, Hilfe bei der Organisation.',
        wordGoal: 'ca. 120–150',
        letter: 'Liebe Maria,\n\nvielen Dank für deine E-Mail! Ich freue mich schon sehr auf das Fest im Innenhof. Eine kleine Band wäre natürlich toll, aber ich glaube, eine gute Playlist reicht auch. Für die Kinder könnten wir Sackhüpfen und Eierlaufen organisieren.',
      },
      {
        id: 'results', scene: 'results', nav: 'Ergebnis', section: 'Ergebnis', clockSeconds: null,
        title: 'Know exactly where you stand.',
        body: 'Points for every written section against the 135-point pass mark, with an explanation for every Leseverstehen and Sprachbausteine answer.',
        showTotal: true, verdict: 'Bestanden · {{pct}} %',
        bars: [
          { name: 'Leseverstehen', got: 60, max: 75 },
          { name: 'Sprachbausteine', got: 22.5, max: 30 },
          { name: 'Hören', got: 55, max: 75 },
          { name: 'Schreiben', got: 33, max: 45 },
        ],
        explanation: {
          label: 'Lücke 21', answer: 'a',
          text: 'Richtig ist A („gemeldet“). „sich melden“ bildet das Perfekt mit „haben“ und dem Partizip „gemeldet“. „melden“ ist der Infinitiv, „meldete“ das Präteritum; beide passen nicht zu „habe“.',
        },
      },
    ],
    redPen: {
      level: 'b1',
      redemittel: { label: 'Redemittel · Sprechen Teil 1', phrase: 'Wie wäre es, wenn wir …?', note: 'Vorschlag machen', meaning: 'How about if we …?', count: 71 },
    },
  },

  'osd-b2': {
    source: 'osd/b2/osd-deutsch-b2-mock-001 - Claude 1.json',
    paperLabel: 'ÖSD Zertifikat B2 · Practice paper 1',
    chapters: [
      {
        id: 'reading', scene: 'questions', nav: 'Lesen', section: 'Lesen · Aufgabe 1', clockSeconds: 90 * 60,
        title: 'Read an Austrian weekly, closely.',
        body: 'A long article and questions with three options each. Lesen and Sprachbausteine share 90 minutes.',
        reading: {
          title: 'Wenn WhatsApp zur Belastung wird',
          text: 'Die ständige Erreichbarkeit über Messenger-Dienste prägt zunehmend die Art, wie Österreicherinnen und Österreicher ihre zwischenmenschlichen Beziehungen gestalten. Was einst als praktische Kommunikationserleichterung begann, entwickelt sich für viele zu einer psychischen Belastung.',
        },
        rows: [
          {
            num: 1, text: 'Die Studie von Dr. Elisabeth Reiter zeigt, dass viele Menschen',
            options: [
              { id: 'A', text: 'ihr Handy bewusst für mehrere Stunden ausschalten.' },
              { id: 'B', text: 'sich gestresst fühlen, ihr Handy aber trotzdem kaum weglegen können.' },
              { id: 'C', text: 'sich vor allem über unhöfliche Nachrichten ärgern.' },
            ],
            answer: 'B',
          },
        ],
      },
      {
        id: 'grammar', scene: 'endings', nav: 'Sprachbausteine', section: 'Sprachbausteine · Aufgabe 3', clockSeconds: 51 * 60 + 20,
        title: 'The cut-off right edge.',
        body: 'The right margin of a newspaper article is missing. Write the last letters of each line back in, three at most.',
        label: 'Neue Studie: Stadtgrün reduziert Stress erheblich',
        lines: [
          { text: 'Eine umfangreiche Studie d', answer: 'er' },
          { text: 'Forschungsgruppe „Stadt und Gesundheit“ hat erstmals wissenschaftl', answer: 'ich' },
          { text: 'nachgewiesen, dass städtis', answer: 'che' },
          { text: 'Grünflächen einen messbar', answer: 'en' },
          { text: 'Einfluss auf d', answer: 'as' },
        ],
      },
      {
        id: 'listening', scene: 'questions', nav: 'Hören', section: 'Hörverstehen · Aufgabe 1', clockSeconds: 30 * 60,
        title: 'A radio broadcast, heard twice.',
        body: 'Decide whether each statement is right or wrong. The recording is played twice, as in the real exam.',
        audio: { label: 'Radiosendung', note: 'Sie hören den Text zwei Mal.' },
        rows: [
          { num: 1, text: 'Laut Maria Lindner ist man heute mit 25 Jahren meistens mit der Ausbildung fertig.', labels: TF, answer: 1 },
          { num: 2, text: 'Laut Dr. Maria Lindner sind Menschen zwischen 30 und 50 Jahren besonders aktiv in der Weiterbildung.', labels: TF, answer: 0 },
        ],
      },
      {
        id: 'writing', scene: 'writing', nav: 'Schreiben', section: 'Schreiben · Aufgabe 1', clockSeconds: 90 * 60,
        title: 'Complain, point by point.',
        body: 'A complaint email of about 120 words that takes up every one of your notes, with a live word count. Submit it and you get a score and feedback.',
        taskLabel: 'Aufgabe 1 · Beschwerde-E-Mail',
        task: 'Ihr Sommerpraktikum bei der Wiener Karriere Agentur entsprach überhaupt nicht der Anzeige. Schreiben Sie an Mag. Petra Hofmann und gehen Sie auf alle Ihre Notizen ein.',
        wordGoal: 'ca. 120',
        letter: 'Sehr geehrte Frau Mag. Hofmann,\n\nim Sommer habe ich bei Ihrer Agentur ein Praktikum absolviert. Leider entsprach es überhaupt nicht den Angaben in Ihrer Anzeige.\n\nStatt kreativer Projekte musste ich fast nur kopieren und Ablage machen. Außerdem habe ich nicht 24, sondern über 35 Stunden pro Woche gearbeitet.',
      },
      {
        id: 'results', scene: 'results', nav: 'Ergebnis', section: 'Ergebnis', clockSeconds: null,
        title: 'Pass every part, not just the total.',
        body: 'ÖSD B2 needs 42 of 70 written points and at least half in Lesen, Hören and Schreiben. You see each one, with an explanation for every Lesen and Sprachbausteine answer.',
        showTotal: true, verdict: 'Bestanden · {{score}} von 70',
        bars: [
          { name: 'Lesen und Sprachbausteine', got: 14, max: 20, pass: 0.5 },
          { name: 'Hören', got: 13, max: 20, pass: 0.5 },
          { name: 'Schreiben', got: 24, max: 30, pass: 0.5 },
        ],
        explanation: {
          label: 'Sprachbausteine · Zeile 2', answer: 'ich',
          text: 'Richtig ist „ich“: „… hat erstmals wissenschaftlich nachgewiesen, …“.',
        },
      },
    ],
    redPen: {
      level: 'b2',
      redemittel: { label: 'Redemittel · Sprechen Teil 3', phrase: 'Da muss ich Ihnen entschieden widersprechen.', note: 'widersprechen', meaning: 'I must strongly disagree with you there.', count: 32 },
    },
  },

  'osd-c1': {
    source: 'osd/c1/osd-deutsch-c1-mock-001 - Claude 1.json',
    paperLabel: 'ÖSD Zertifikat C1 · Practice paper 1',
    chapters: [
      {
        id: 'reading', scene: 'questions', nav: 'Lesen', section: 'Lesen · Aufgabe 1', clockSeconds: 90 * 60,
        title: 'A long magazine article, read closely.',
        body: 'Questions with three options each, on a demanding Austrian text. Lesen and Sprachbausteine share 90 minutes.',
        reading: {
          title: 'Immer erreichbar, nie ganz da',
          text: 'Für die Grazer Soziologin Katharina Leitner ist das kein Widerspruch. „Wir haben nicht weniger Zeit als früher, aber wir erleben sie anders“, sagt sie. Leitner spricht von „Gleichzeitigkeit“: Die Grenzen zwischen Arbeit und Privatleben lösen sich auf.',
        },
        rows: [
          {
            num: 1, text: 'Mit dem Begriff „Gleichzeitigkeit“ beschreibt Katharina Leitner,',
            options: [
              { id: 'A', text: 'dass Menschen heute insgesamt mehr arbeiten als früher.' },
              { id: 'B', text: 'dass sich Arbeit und Privatleben kaum noch trennen lassen.' },
              { id: 'C', text: 'dass technische Geräte immer schneller reagieren.' },
            ],
            answer: 'B',
          },
        ],
      },
      {
        id: 'grammar', scene: 'gap', nav: 'Sprachbausteine', section: 'Sprachbausteine · Aufgabe 4', clockSeconds: 52 * 60,
        title: 'No options. Just the right word.',
        body: 'At C1, Sprachbausteine is a free-text cloze: you write each missing word yourself.',
        label: 'Traditionelle Handwerkskunst im 21. Jahrhundert',
        gapNumber: 1,
        before: 'Die österreichische Manufaktur „Alpenwerkstatt“ wurde vor über hundert Jahren im Salzkammergut gegründet und steht heute',
        after: 'eine zeitgemäße Verbindung von jahrhundertealter Handwerkstradition und moderner Designphilosophie.',
        answer: 'für',
      },
      {
        id: 'listening', scene: 'questions', nav: 'Hören', section: 'Hören · Aufgabe 1', clockSeconds: 40 * 60,
        title: 'An eleven-minute radio feature, heard twice.',
        body: 'A feature on the attention economy, with three options per question. The recording is played twice, as in the real exam.',
        audio: { label: 'Radiobeitrag', note: 'Sie hören den Text zwei Mal.' },
        rows: [
          {
            num: 1, text: 'Herbert Simons Warnung aus den 1970er Jahren',
            options: [
              { id: 'A', text: 'hat sich auf unerwartete Weise bewahrheitet.' },
              { id: 'B', text: 'wurde von der Gesellschaft weitgehend ignoriert.' },
              { id: 'C', text: 'bezog sich hauptsächlich auf technische Entwicklungen.' },
            ],
            answer: 'A',
          },
        ],
      },
      {
        id: 'writing', scene: 'writing', nav: 'Schreiben', section: 'Schreiben · Aufgabe 1', clockSeconds: 90 * 60,
        title: 'Answer the email, using your notes.',
        body: 'A formal reply that takes up every note you made, with a live word count. Submit it and you get a score and feedback.',
        taskLabel: 'Aufgabe 1 · Antwort-E-Mail',
        task: 'Die Akademie hat Ihre Teilnahme am Zertifikatslehrgang „Digitale Transformation“ abgesagt. Antworten Sie und berücksichtigen Sie dabei Ihre Notizen.',
        letter: 'Sehr geehrter Herr Mag. Dr. Steiner,\n\nIhre Absage habe ich mit großem Befremden zur Kenntnis genommen. Entgegen Ihrer Darstellung habe ich sämtliche Unterlagen bereits am 18. Februar per E-Mail übermittelt, eine Bestätigung jedoch nie erhalten. Zudem bin ich seit acht Jahren als Abteilungsleiterin in einem großen Technologiekonzern tätig.',
      },
      {
        id: 'results', scene: 'results', nav: 'Ergebnis', section: 'Ergebnis', clockSeconds: null,
        title: 'Pass every part, not just the total.',
        body: 'ÖSD C1 needs 42 of 70 written points, at least half in Lesen and Hören, and 7 of 15 in each writing task. You see each one, with an explanation for every Lesen and Sprachbausteine answer.',
        showTotal: true, verdict: 'Bestanden · {{score}} von 70',
        bars: [
          { name: 'Lesen und Sprachbausteine', got: 14, max: 20, pass: 0.5 },
          { name: 'Hören', got: 13, max: 20, pass: 0.5 },
          { name: 'Schreiben · Aufgabe 1', got: 11, max: 15, pass: 7 / 15 },
          { name: 'Schreiben · Aufgabe 2', got: 12, max: 15, pass: 7 / 15 },
        ],
        explanation: {
          label: 'Lesen · Aufgabe 1', answer: 'B',
          text: 'Richtig ist B. Leitner erklärt: „Die Grenzen zwischen Arbeit und Privatleben lösen sich auf.“',
        },
      },
    ],
    redPen: {
      level: 'c1',
      redemittel: { label: 'Redemittel · Sprechen Teil 2', phrase: 'Diese Betrachtungsweise vernachlässigt jedoch einen wesentlichen Aspekt.', note: 'Einwand', meaning: 'However, this perspective neglects an essential aspect.', count: 40 },
    },
  },
};


// Row 3 (red-pen cards): grammar and vocabulary are per LEVEL, shared by the three
// providers. Each grammar card is a real learner error, never a correct variant
// marked wrong. Counts are measured from the data files:
//   grammar-levels/{lvl}-grammar_en.json  pillars: B1 11, B2 8, C1 8
//   vocabulary/vocabulary-{lvl}_en.json   cards/topics: B1 770/8, B2 701/9, C1 980/9
// and each exam's Redemittel count from redemittel/{provider}-{lvl}-redemittel.json.
export const RED_PEN_LEVEL = {
  b1: {
    grammar: {
      label: 'Grammatik · Verben mit Präposition',
      before: 'Ich habe mich schon gut', insert: 'an', after: 'die neue Stadt gewöhnt.',
      note: 'sich gewöhnen an + Akk.',
      explain: '‘sich gewöhnen an + Akkusativ’: the preposition belongs to the verb.',
      pillars: 11,
    },
    vocab: {
      label: 'Wortschatz · Alltag und Wohnen', article: 'das', word: 'Einfamilienhaus', note: 'Artikel mitlernen!',
      meaning: 'single-family house, detached house',
      example: 'Viele Familien träumen von einem Einfamilienhaus im Grünen.',
      count: 770, topics: 8,
    },
  },
  b2: {
    // From the B2 Passiv-Ersatz exercise (ps-alt-001): the reflexive is missing.
    grammar: {
      label: 'Grammatik · Passiv-Ersatz',
      before: 'Dieses Problem lässt', insert: 'sich', after: 'leicht lösen.',
      note: 'sich lassen + Infinitiv',
      explain: '‘sich lassen + Infinitiv’ replaces können + Passiv.',
      pillars: 8,
    },
    vocab: {
      label: 'Wortschatz · Arbeitswelt', article: 'die', word: 'Festanstellung', note: 'Artikel mitlernen!',
      meaning: 'permanent position',
      example: 'Im Gegensatz zur Projektarbeit bietet die Festanstellung eine höhere Arbeitsplatzsicherheit.',
      count: 701, topics: 9,
    },
  },
  c1: {
    grammar: {
      label: 'Grammatik · Adjektive mit Präposition',
      before: 'Viele Studierende sind', insert: 'mit', after: 'der neuen Regelung unzufrieden.',
      note: 'unzufrieden mit + Dat.',
      explain: '‘unzufrieden mit + Dativ’: the fixed preposition decides the case.',
      pillars: 8,
    },
    vocab: {
      label: 'Wortschatz · Wirtschaft', article: 'die', word: 'Volkswirtschaft', note: 'Artikel mitlernen!',
      meaning: 'national economy, economics',
      example: 'Die Volkswirtschaft eines Landes umfasst die Gesamtheit aller wirtschaftlichen Aktivitäten.',
      count: 980, topics: 9,
    },
  },
};

export const getExamShowcase = (slug) => EXAM_SHOWCASE[slug] || null;

/** The red-pen row's content for an exam: its level's cards plus its Redemittel. */
export function getRedPen(showcase) {
  if (!showcase?.redPen) return null;
  const { level, redemittel } = showcase.redPen;
  return { ...RED_PEN_LEVEL[level], redemittel, level };
}
